import { IntlShape } from 'react-intl';
import { InfoWidgetType, PlayerWidgetType, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { linkButtonLabel, linkHost } from 'fym-shared/src/smartlinks/linkLabel';
import { mediaBlockDetail, mediaBlockLabel, isMediaBlockHidden } from '../../widgets/views/mediaWidget/mediaBlockLabel';
import { presaveSubscribeBlockDetail } from '../../widgets/views/presaveSubscribeWidget/presaveSubscribeServices';
import { blockSummaryMessages as m } from './blockSummaryMessages';
import { describeCatalogLinks } from './describeCatalogLinks';
import { linkTypeName } from './linkTypeName';

/** The summary line colour (design d0, 5): amber when the page hides the block, green when it updates by itself. */
export type BlockSummaryTone = 'normal' | 'warn' | 'pos';

export interface BlockDescription {
  /** The block's own name: the button text, the first words of the text, the song. */
  name: string;
  /** The small type tag after the name. Null when the name is the type already. */
  typeLabel: string | null;
  summary: string;
  tone: BlockSummaryTone;
}

export interface DescribeBlockContext {
  /** The artist name, for the Profile row. */
  artistName: string | null;
}

const FIRST_WORDS_LENGTH = 32;

/** The first words of a text, cut at a word end: "Written in one night in a cabin…". */
export function firstWords(text: string, length: number = FIRST_WORDS_LENGTH): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= length) {
    return flat;
  }
  const cut = flat.slice(0, length);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > length / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function blockTypeLabel(intl: IntlShape, type: SmartLinkWidgetType): string {
  switch (type) {
    case SmartLinkWidgetType.info:
      return intl.formatMessage(m.typeText);
    case SmartLinkWidgetType.link:
      return intl.formatMessage(m.typeLink);
    case SmartLinkWidgetType.player:
      return intl.formatMessage(m.typePlayer);
    case SmartLinkWidgetType.media:
      return intl.formatMessage(m.typeMedia);
    case SmartLinkWidgetType.socialLinks:
      return intl.formatMessage(m.typeSocial);
    case SmartLinkWidgetType.profile:
      return intl.formatMessage(m.typeProfile);
    case SmartLinkWidgetType.presave:
      return intl.formatMessage(m.typeCatalogLinks);
    case SmartLinkWidgetType.presaveSubscribe:
      return intl.formatMessage(m.typeSubscribe);
    case SmartLinkWidgetType.image:
      return intl.formatMessage(m.typeImage);
    default:
      return intl.formatMessage(m.typeUnknown);
  }
}

function styleLabel(intl: IntlShape, type: InfoWidgetType): string {
  if (type === InfoWidgetType.title) {
    return intl.formatMessage(m.styleTitle);
  }
  return intl.formatMessage(type === InfoWidgetType.small ? m.styleSmall : m.styleNormal);
}

/** The name, the summary and its colour of one block, before the hidden flag (design d0). */
function describeContent(intl: IntlShape, widget: SerializedWidget, context: DescribeBlockContext): BlockDescription {
  const typeLabel = blockTypeLabel(intl, widget.type);
  switch (widget.type) {
    case SmartLinkWidgetType.info: {
      const words = firstWords(widget.data.text);
      const style = styleLabel(intl, widget.data.type);
      return {
        name: words === '' ? typeLabel : firstWords(widget.data.text, 24),
        typeLabel: words === '' ? null : typeLabel,
        summary: words === '' ? intl.formatMessage(m.emptyText) : intl.formatMessage(m.textSummary, { style, words }),
        tone: 'normal',
      };
    }
    case SmartLinkWidgetType.link: {
      const { url, label, type, smartLinkId } = widget.data;
      const button = linkButtonLabel({ title: label, url, linkType: type });
      let name = typeLabel;
      if (button.kind === 'title') {
        name = button.text;
      } else if (button.kind === 'service') {
        name = intl.formatMessage(button.verb === 'watch' ? m.watchOn : m.listenOn, { service: button.serviceName });
      } else if (button.kind === 'host') {
        name = button.host;
      }
      const host = linkHost(url);
      const summary =
        smartLinkId !== undefined && smartLinkId !== null
          ? intl.formatMessage(m.linkOpensPage)
          : (host ?? intl.formatMessage(m.linkNoAddress));
      return { name, typeLabel, summary, tone: host === null && smartLinkId === undefined ? 'warn' : 'normal' };
    }
    case SmartLinkWidgetType.player: {
      const service = linkTypeName(widget.data.musicServiceType) ?? linkHost(widget.data.url) ?? typeLabel;
      const size = intl.formatMessage(widget.data.type === PlayerWidgetType.compact ? m.sizeCompact : m.sizeNormal);
      return {
        name: intl.formatMessage(m.playerName, { service }),
        typeLabel,
        summary: intl.formatMessage(m.playerSummary, { service, size }),
        tone: 'normal',
      };
    }
    case SmartLinkWidgetType.media:
      return {
        name: mediaBlockLabel(intl, widget.data),
        typeLabel,
        summary: mediaBlockDetail(intl, widget.data),
        tone: isMediaBlockHidden(widget.data) ? 'warn' : 'normal',
      };
    case SmartLinkWidgetType.socialLinks: {
      const links = widget.data.socialLinks;
      const names = links.map((link) => linkTypeName(link.type) ?? linkHost(link.url) ?? link.type).join(', ');
      return links.length === 0
        ? { name: typeLabel, typeLabel: null, summary: intl.formatMessage(m.socialEmpty), tone: 'warn' }
        : {
            name: typeLabel,
            typeLabel: null,
            summary: intl.formatMessage(m.socialSummary, { count: links.length, names }),
            tone: 'normal',
          };
    }
    case SmartLinkWidgetType.profile:
      return {
        name: context.artistName ?? typeLabel,
        typeLabel: context.artistName === null ? null : typeLabel,
        summary: intl.formatMessage(m.profileSummary),
        tone: 'normal',
      };
    case SmartLinkWidgetType.presave:
      return { name: widget.data.trackTitle, typeLabel, ...describeCatalogLinks(intl, widget) };
    case SmartLinkWidgetType.presaveSubscribe:
      return {
        name: typeLabel,
        typeLabel: null,
        summary: presaveSubscribeBlockDetail(intl, widget.data.services),
        tone: 'normal',
      };
    case SmartLinkWidgetType.image:
      return {
        name: intl.formatMessage(m.imageName),
        typeLabel,
        summary: intl.formatMessage(m.imageSummary),
        tone: 'warn',
      };
    default:
      return { name: typeLabel, typeLabel: null, summary: '', tone: 'normal' };
  }
}

/** The row text of a block (design d0). A hidden block says so first, in amber. */
export function describeBlock(
  intl: IntlShape,
  widget: SerializedWidget,
  context: DescribeBlockContext
): BlockDescription {
  const description = describeContent(intl, widget, context);
  if (widget.hidden !== true) {
    return description;
  }
  return {
    ...description,
    summary:
      description.summary === ''
        ? intl.formatMessage(m.hiddenOnly)
        : intl.formatMessage(m.hiddenPrefix, { summary: description.summary }),
    tone: 'warn',
  };
}
